import { Badge, Flex, Text } from '@mantine/core';
import {
	DataKYCFileTctWithFile,
	KycEventLogHistoryRes,
	SubjectAction,
} from '@dealstack/shared/lib/generated';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import {
	useTranslationMipCreate,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DocumentStatusDot } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/DocumentStatusDot';
import { REQUESTED_STATUS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/components/utils';
import {
	BadgeColumn,
	CardBody,
	CardContainer,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/styled';
import { getFormCardDocumentStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/utils/getFormCardDocumentStatus';
import { findFormUploaderName } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/utils/findFormUploaderName';

interface Props {
	primaryText: string;
	kycFile?: DataKYCFileTctWithFile;
	kycHistory?: KycEventLogHistoryRes;
	isSmartForm?: boolean;
	isSelected?: boolean;
	onClick?: () => void;
}

export const FormReviewCard = ({
	primaryText,
	kycFile,
	kycHistory,
	isSmartForm = false,
	isSelected = false,
	onClick,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const tMipCreate = useTranslationMipCreate();
	const isInherited = kycFile?.subjectAction === SubjectAction.NOT_YET_ACTED;
	const status = isInherited ? REQUESTED_STATUS : getFormCardDocumentStatus(kycFile);

	const uploadedAt = kycFile?.createdAt
		? formatStringDateToUI(kycFile.createdAt, DATE_FORMATS.CALENDAR_DATE)
		: undefined;
	const uploaderName = kycFile ? findFormUploaderName(kycFile, kycHistory) : undefined;

	return (
		<CardContainer onClick={onClick} $isSelected={isSelected}>
			<CardBody>
				<Text size="B6Bold" c="dark.8" truncate lineClamp={2}>
					{primaryText}
				</Text>

				{uploadedAt && (
					<Text size="B6" c="dark.2">
						{uploaderName
							? `${tMipManage('kyc.review.forms.uploaded_by')} ${uploaderName} | ${uploadedAt}`
							: `${tMipManage('kyc.review.forms.uploaded_on')} ${uploadedAt}`}
					</Text>
				)}

				<Flex mt="0.25rem">
					<DocumentStatusDot status={status} isInherited={isInherited} />
				</Flex>
			</CardBody>

			{isSmartForm && (
				<BadgeColumn>
					<Badge size="xs" variant="outline" color="gray.4" c="dark">
						<TextV2 variant="sans/11/400" center noWrap>
							{tMipCreate('kyc.documents.card.smart_form')}
						</TextV2>
					</Badge>
				</BadgeColumn>
			)}
		</CardContainer>
	);
};
